<template>
    <div ref="chart" style="width: 100%; height: 500px"></div>
</template>

<script>
import * as echarts from "echarts";
import { ref, onMounted, computed, onBeforeUnmount } from "vue";
import store from "@/store/store";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import { dateFormatAsText, getCurrencySymbol, getExchangeRateFromCzk } from "@/helpers/data";
import { getItem } from "@/store/persistantStorage";
import { formatRevenue } from "@/helpers/graphs";

export default {
    name: "WebsRevenueStackedLineChart",
    props: {
        earningsCsvData: {
            type: Array,
            required: true,
        },
    },
    setup(props) {
        // Čtení currency, exchange rate a symbolu z úložiště/storu
        const selectCurrency = computed(() => store.getters[publisherGetterTypes.getSelectedPublisherCurrency]);
        const currencySymbol = computed(() => getCurrencySymbol(selectCurrency.value));
        const exchangeRate = computed(() => getExchangeRateFromCzk(selectCurrency.value));

        const chart = ref(null);
        let myChart = null;
        const isLightTheme = computed(() => getItem("theme") == "light");
        const selectedWebsites = ref([]); // Multi-select filter for websites

        // Get list of unique websites and dates
        const getWebsites = computed(() => {
            return [...new Set(props.earningsCsvData.map((item) => item.website))].sort();
        });
        const getDates = computed(() => {
            const allDates = props.earningsCsvData.map((item) => item.date);
            return [...new Set(allDates)].sort((a, b) => new Date(a) - new Date(b));
        });

        // Transform data so that each series (website) has a value for every day from the global list.
        // If there's no record for a given date, return 0.
        const transformData = computed(() => {
            const websites = getWebsites.value;
            const dates = getDates.value;

            // Create map where each website has date => revenue mapping
            const groupedData = websites.reduce((acc, website) => {
                acc[website] = {};
                props.earningsCsvData
                    .filter((item) => item.website === website)
                    .forEach((item) => {
                        // Calculate revenue using exchangeRate
                        const revenue = Math.round((item["publisher revenue"] / exchangeRate.value) * 100) / 100;
                        acc[website][item.date] = revenue;
                    });
                return acc;
            }, {});

            // Create series for each website, adding value for each date or 0
            // If website is selected, show data, otherwise show zeros
            const series = websites.map((website) => {
                const isSelectedOrShowAll =
                    selectedWebsites.value.length === 0 || selectedWebsites.value.includes(website);
                const websiteDataMap = groupedData[website] || {};
                const seriesData = dates.map((date) => {
                    if (!isSelectedOrShowAll) return 0; // If website not selected, return 0
                    return websiteDataMap[date] !== undefined ? websiteDataMap[date] : 0;
                });

                return {
                    name: website,
                    type: "bar",
                    stack: "Total",
                    data: seriesData,
                    itemStyle: {
                        borderWidth: 0,
                        borderColor: "#ffffff",
                    },
                    label: {
                        show: dates.length < 60,
                        position: "inside",
                        formatter: (params) => formatRevenue(params, currencySymbol.value),
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                };
            });

            return series.filter((serie) => serie.data.length > 0);
        });

        // Computed property for legend selected state
        const legendSelected = computed(() => {
            const selected = {};
            getWebsites.value.forEach((website) => {
                // If no filter, all websites are selected
                // If filter active, only selected websites are selected
                selected[website] = selectedWebsites.value.length === 0 || selectedWebsites.value.includes(website);
            });
            return selected;
        });

        // Function for toggling website filter (multi-select)
        const toggleWebFilter = (websiteName) => {
            const currentIndex = selectedWebsites.value.indexOf(websiteName);
            if (currentIndex > -1) {
                // Website already selected → remove from list
                selectedWebsites.value.splice(currentIndex, 1);
            } else {
                // Website not selected → add to list
                selectedWebsites.value.push(websiteName);
            }

            // Force update chart with new data and legend state
            if (myChart) {
                myChart.setOption({
                    series: transformData.value,
                    legend: {
                        selected: legendSelected.value,
                    },
                });
            }
        };

        onMounted(() => {
            myChart = echarts.init(chart.value);
            const option = {
                title: {
                    text: "Revenue",
                    textStyle: {
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                tooltip: {
                    trigger: "axis",
                    formatter: function (params) {
                        let result = "<strong>" + dateFormatAsText(params[0].name) + "</strong><br/>";
                        let totalValue = 0;
                        params.forEach(function (item) {
                            totalValue += item.value;
                        });
                        result += `<div style="padding-bottom: 0.25em;">Total: ${parseFloat(totalValue).toFixed(2)} ${
                            currencySymbol.value
                        }</div>`;
                        params.forEach(function (item) {
                            result +=
                                item.marker +
                                item.seriesName +
                                ": " +
                                parseFloat(item.value).toFixed(2) +
                                " " +
                                currencySymbol.value +
                                "<br/>";
                        });
                        return result;
                    },
                },
                legend: {
                    data: getWebsites.value,
                    selected: legendSelected.value,
                    ...(getWebsites.value.length >= 7
                        ? {
                              right: "1%",
                              top: "8.5%",
                              orient: "vertical",
                              itemGap: 10,
                          }
                        : {
                              bottom: 0,
                              itemGap: 25,
                          }),
                    type: getWebsites.value.length > 10 ? "scroll" : "plain",
                    textStyle: {
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                        fontSize: getWebsites.value.length >= 7 ? 14 : 15,
                        fontFamily: "Open Sans",
                    },
                },
                grid: {
                    left: "1%",
                    right: getWebsites.value.length >= 7 ? "14%" : "1%",
                    top: "10%",
                    bottom: getWebsites.value.length >= 7 ? "5%" : "10%",
                    containLabel: true,
                },
                xAxis: {
                    type: "category",
                    boundaryGap: true,
                    data: getDates.value,
                    axisLabel: {
                        formatter: dateFormatAsText,
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                yAxis: {
                    type: "value",
                    axisLine: {
                        lineStyle: {
                            color: isLightTheme.value ? "#000000" : "#ffffff",
                        },
                    },
                    axisLabel: {
                        formatter: function (value) {
                            return value + " " + currencySymbol.value;
                        },
                        color: isLightTheme.value ? "#000000" : "#ffffff",
                    },
                },
                series: transformData.value,
            };
            myChart.setOption(option);

            // Legend click handler for filtering
            myChart.on("legendselectchanged", (params) => {
                // Apply our toggle logic (already updates legend state)
                toggleWebFilter(params.name);
            });
        });

        onBeforeUnmount(() => {
            if (myChart) {
                myChart.dispose();
            }
        });

        return {
            chart,
        };
    },
};
</script>

<style scoped></style>
